import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import TerminationPopUp from "../TerminationFinalExit/TerminationPopUp.js/TerminationPopUp";
import { GoEye } from "react-icons/go";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  RESIGNATION_COLUMNS,
  RESIGNATION_CONFIG,
  RESIGNATION_SEARCHABLE_FIELDS,
  renderVisaProvider,
  renderResignationStatus
} from './ResignationConstants';

const ResignationList = ({
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Popup state management
  const [popupState, setPopupState] = useState(() => ({
    resignation: false,
    propData: null
  }));

  // Handle eye icon click to open popup
  const handleViewClick = useCallback((item) => {
    setPopupState({
      resignation: true,
      propData: item
    });
  }, []);

  // Close popup handler
  const handleClosePopup = useCallback(() => {
    setPopupState({
      resignation: false,
      propData: null
    });
  }, []);

  // Custom cell renderer for special cases
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return renderVisaProvider(item);
      
      case 'total_salary':
        return item?.total_salary?.toString() || "-";
      
      case 'resignation_aging':
        return "0";
      
      case 'resignation_status':
        return renderResignationStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "final-exit-edit") ? (
          <div
            className="ml-2 cursor-pointer"
            onClick={() => handleViewClick(item)}
          >
            <GoEye fontSize={22} className="ml-2" />
          </div>
        ) : null;
      
      default:
        return null;
    }
  }, [handleViewClick, dashboardData]);

  // Download handlers
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={RESIGNATION_CONFIG.API_ENDPOINT}
        downloadEndpoint={RESIGNATION_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={RESIGNATION_COLUMNS}
        searchableFields={RESIGNATION_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={RESIGNATION_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={RESIGNATION_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={RESIGNATION_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={RESIGNATION_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={RESIGNATION_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={RESIGNATION_CONFIG.NO_DATA_MESSAGE}
        className="resignation-table"
      />

      {/* Popup rendering */}
      {popupState.resignation && (
        <TerminationPopUp
          PopUp={popupState.resignation}
          setPopUp={handleClosePopup}
          setTrigger={setTrigger}
          trigger={trigger}
          propData={popupState.propData}
          endpoint="resignation"
        />
      )}
    </div>
  );
};

ResignationList.displayName = 'ResignationList';

export default ResignationList;
